import {
  type BibleChooserData,
  getBibleChooserDataFromString,
  isValidBibleChooserData,
  quickID,
} from '@soulhx/fs-common';
import { clsx, PlainTextEdit, SHXButton, StandardButton } from '@soulhx/ui-common';
import React, { useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { SpecificVersion } from './SpecificVersion.js';
import { useUserSetting } from '@soulhx/client-store';

interface IBibleVersionSearch {
  chooserData: BibleChooserData[];
  update: (newData: BibleChooserData[]) => void;
  className?: string;
}
export const BibleVersionSearch = ({ chooserData, update, className }: IBibleVersionSearch) => {
  const { settingValue: defaultVersion } = useUserSetting<string>('readDefaultVersion');
  const [editedList, setEditedList] = useState<BibleChooserData[]>(
    chooserData.length > 0 ? chooserData : [{ abbreviation: '', abbreviationLocal: '', bibleId: '' }]
  );
  const [isEditing, setIsEditing] = useState(false);
  const { t } = useTranslation(['bibleviewer']);

  const UseDefaultButton = useMemo(() => {
    const isInputValid = isValidBibleChooserData({ inputArray: editedList });
    const isDefaultValid = isValidBibleChooserData({ inputString: defaultVersion });

    if (isInputValid) return null;

    // handles old-style versions, that are just a TLA (only populated in abbreviation field)
    // just needs to kick off editing
    if (editedList.length === 1 && editedList[0].abbreviation.length > 0) {
      return (
        <div>
          <SHXButton
            variant="warning"
            onClick={() => {
              setEditedList([{ abbreviation: '', abbreviationLocal: editedList[0].abbreviation, bibleId: '' }]);
              setIsEditing(true);
            }}
          >
            {t('bibleviewer:versionSearch.searchForVersion', { val: editedList[0].abbreviation })}
          </SHXButton>
        </div>
      );
    }

    if (isDefaultValid) {
      return (
        <div>
          <SHXButton
            variant="secondary"
            onClick={() => {
              const newList = getBibleChooserDataFromString(defaultVersion!);
              setEditedList(newList);
              update(newList);
            }}
          >
            {t('bibleviewer:versionSearch.useDefaultVersion', {
              val: getBibleChooserDataFromString(defaultVersion!).map((v) => v.abbreviationLocal),
              count: getBibleChooserDataFromString(defaultVersion!).map((v) => v.abbreviationLocal).length,
            })}
          </SHXButton>
        </div>
      );
    }

    if (defaultVersion.length > 0) {
      return (
        <div>
          <SHXButton
            variant="warning"
            onClick={() => {
              const newList = editedList.slice();
              newList[0].abbreviationLocal = defaultVersion;
              setEditedList(newList);
              setIsEditing(true);
            }}
          >
            {t('bibleviewer:versionSearch.searchForVersion', { val: defaultVersion })}
          </SHXButton>
        </div>
      );
    }

    return null;
  }, [editedList, defaultVersion, setEditedList, setIsEditing, update, t]);

  return (
    <div className={clsx('m-0 p-0', className)}>
      {UseDefaultButton}

      {!isEditing && (
        <div className="m-0 p-0">
          <PlainTextEdit
            isReadOnly
            value={t('bibleviewer:versionList', { versions: editedList.map((i) => i.abbreviationLocal) })}
            placeholder={t('bibleviewer:versionSearch.blank')}
            onFocus={() => setIsEditing(true)}
          />
        </div>
      )}

      {isEditing && (
        <div className="bg-light-subtle">
          <Form.Text>{t('bibleviewer:versionSearch.multipleAllowed')}</Form.Text>

          {editedList.map((item, index) => (
            <SpecificVersion
              chooserData={item}
              key={quickID()}
              update={(newItem) => {
                const newChooserData = editedList.slice();
                newChooserData[index].abbreviation = newItem.abbreviation;
                newChooserData[index].abbreviationLocal = newItem.abbreviationLocal;
                newChooserData[index].bibleId = newItem.bibleId;
                setEditedList(newChooserData);
                if (isValidBibleChooserData({ inputArray: newChooserData })) {
                  update(newChooserData);
                  setIsEditing(false);
                }
              }}
              onlyOne={editedList.length === 1}
              remove={() => {
                const newList = editedList.filter((i) => i.bibleId !== item.bibleId);
                setEditedList(newList);
              }}
            />
          ))}

          <div className="mt-3 text-end">
            <StandardButton
              type="save"
              onClick={() => {
                const newList = editedList.filter(
                  (b) => b.abbreviation.length > 0 && b.abbreviationLocal.length > 0 && b.bibleId.length > 0
                );
                setEditedList(newList);
                update(newList);
                setIsEditing(false);
              }}
              disabled={!isValidBibleChooserData({ inputArray: editedList })}
            />

            <StandardButton
              type="add"
              onClick={() => {
                const newList = editedList.slice();
                newList.push({ abbreviation: '', abbreviationLocal: '', bibleId: '' });
                setEditedList(newList);
              }}
            />

            <StandardButton
              type="cancel"
              onClick={() => {
                setEditedList(chooserData);
                setIsEditing(false);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
